"use client";

import { useState } from "react";

// eslint-disable-next-line import/named
import { FieldValues, useForm } from "react-hook-form";
import { useImmer } from "use-immer";
import { preSubmit } from "@/forms/validation";
import { getCaptcha } from "@/forms/helpers";
import { clientPost } from "@/service/client-api/client-api";
import { trans } from "@/service/i18n/i18n";

import { useEnvContext } from "next-runtime-env";
import { vestResolver } from "@hookform/resolvers/vest";
import { fromFormDefinitionToValidationSchema } from "@/forms/validation/validation-factory";
import { RecaptchaField } from "@/forms/fields/recaptcha-field";
import { TextareaField } from "@/forms/fields/textarea-field/textarea-field";
import { Form } from "@/components/ui/form";
import { model } from "./model";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { handleUserError } from "@/service/client-api/error-handler";

export function CourseAskQuestionForm() {
  const [fields, setFields] = useImmer(model);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const { NEXT_PUBLIC_RECAPTCHA_SITE_KEY } = useEnvContext();

  const form = useForm({
    resolver: vestResolver(fromFormDefinitionToValidationSchema(fields)),
  });
  const { handleSubmit, reset } = form;

  const onSubmit = async ({ message }: FieldValues) => {
    if (preSubmit(loading, setLoading, setError, setFields) === "BREAK") {
      return;
    }

    try {
      const recaptcha = await getCaptcha(
        "error",
        NEXT_PUBLIC_RECAPTCHA_SITE_KEY as string,
      );

      await clientPost(
        "course.contact",
        {},
        {
          from: window.location.pathname,
          message,
          recaptcha,
        },
      );
      setSuccess(true);
      setLoading(false);

      setFields(() => model);
      reset({ message: "" });
    } catch (caughtError: unknown) {
      setLoading(false);
      handleUserError(caughtError as ServerError, setError, setFields);
    }
  };

  return (
    <Form {...form}>
      <div className="flex flex-col items-stretch gap-6">
        {!success ? (
          <form onSubmit={handleSubmit(onSubmit)}>
            <div>
              <h4 className="text-center text-lg font-semibold">
                Masz pytania?
              </h4>
              <p className="text-muted-foreground text-center text-sm">
                Skontaktuj się z nami – chętnie pomożemy!
              </p>
            </div>

            <TextareaField field={fields.message} />

            <Button
              disabled={loading}
              variant="blue"
              type="submit"
              className="w-full"
            >
              {loading && <Spinner />}
              {trans("contact_page.send")}
            </Button>

            {error && (
              <div
                className="error"
                dangerouslySetInnerHTML={{ __html: error }}
              />
            )}
          </form>
        ) : (
          <div
            dangerouslySetInnerHTML={{
              __html: trans("education.course.ask_form.success"),
            }}
          />
        )}

        <RecaptchaField />
      </div>
    </Form>
  );
}
